import { FhirStatus, StatusAlert, StatusIcon, Title } from "@fyrstain/fhir-front-library";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Client from "fhir-kit-client";
import { Bundle, BundleEntry, Composition, OperationOutcome, OperationOutcomeIssue, Parameters } from "fhir/r5";
import i18n from "i18next";
import { FunctionComponent, useCallback, useState } from "react";
import { Button, Card, Collapse, Form } from "react-bootstrap";
import ProgressBar from 'react-bootstrap/ProgressBar';
import JsonViewer from '../../components/JsonViewer/JsonViewer';
import { useNavigate, useParams } from "react-router-dom";
import { useOnMount } from "src/hooks/useOnMount";
import PandoraPage from "../../components/PandoraPage/PandoraPage";
import styles from "./ValidationReport.module.css";
import { useEnvironment } from "src/context/EnvironmentContext";

const ValidationReport: FunctionComponent = () => {

  const { currentEnvironment } = useEnvironment();
  const { validationReportId } = useParams();
  const [validationReport, setValidationReport] = useState({} as Bundle);
  const [issues, setIssues] = useState([] as OperationOutcomeIssue[]);
  const [loading, setLoading] = useState(false);
  const [testReport, setTestReport] = useState<{ id: string, name: string } | null | undefined>(undefined);
  const [validatorId, setValidatorId] = useState<string | undefined>(undefined);
  const [issuesExpanded, setIssuesExpanded] = useState(true);
  const [selectedIssueSeverity, setSelectedIssueSeverity] = useState<string | undefined>(undefined);

  const fhirClient = new Client({
    baseUrl: currentEnvironment?.validateurUrl ?? process.env.REACT_APP_FHIR_URL ?? 'fhir'
  });

  useOnMount(() => {
    void loadValidationReport();
  });

  /**
    * Get test report to populate the field.
    * 
    * @param bundle    the bundle to the validation report.
    */
  async function getTestReport(bundle: Bundle) {
    const validationReportId = bundle.id || '';
    const response = await fhirClient.search({
      resourceType: 'TestReport',
      searchParams: {
        _elements: '_id,name',
        _filter: 'report-test-link sw "Bundle/' + validationReportId + '" or report-setup-link sw "Bundle/' + validationReportId + '"'
      }
    });
    if (response.entry && response.entry.length > 0) {
      const testReport = response.entry.find((entry: BundleEntry) => entry.resource?.resourceType === 'TestReport');
      if (testReport && testReport.resource) {
        return {
          id: testReport.resource.id,
          name: testReport.resource.name
        };
      }
    }
    throw new Error('Ce rapport d\'évaluation n\'est pas issu d\'un test');
  }

  /**
   * Load Validation report from the back to populate the textarea.
   * 
   * @returns the promise of a Validation report.
   */
  async function loadValidationReport() {
    setLoading(true);
    try {
      const response = await fhirClient.read({
        resourceType: 'Bundle',
        id: validationReportId ?? ''
      })
      const validationReport: Bundle = response as Bundle;
      if (validationReport.entry) {
        const type = (validationReport.entry[0].resource as Composition).type.coding?.filter(c => c.system === "http://isis.com/ValueSet/VS-CompositionSectionCode").map(c => c.code)[0] ?? "";
        console.log(type);
        if (type === "evaluation-report") {
          onEvaluationReport(validationReport.id ?? "");
        }
      }
      setValidationReport(validationReport);
      setIssues(getIssues(validationReport));
      const profileUrl = getProfileUrl(validationReport);
      const reportValidatorId = getValidatorId(validationReport);
      setValidatorId(reportValidatorId ?? await findValidatorIdForProfile(profileUrl));
      setTestReport(undefined);
      getTestReport(validationReport)
        .then(report => setTestReport(report))
        .catch(() => setTestReport(null));
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  }

  const navigate = useNavigate();

  const onEvaluationReport = useCallback((id: string) => {
    navigate("/EvaluationReport/" + id);
  }, [navigate]);

  const toValidator = useCallback((validatorId: string, profileUrl: string) => {
    navigate(`/ValidatorHome/${encodeURIComponent(validatorId)}?profile=${encodeURIComponent(profileUrl)}`);
  }, [navigate]);

  const onError = useCallback((error?: unknown) => {
    navigate("/Error", { state: { error } });
  }, [navigate]);

  /**
    * Get profile URL to populate the field.
    * 
    * @param validationReport    the bundle to the Validation report.
    */
  function getProfileUrl(validationReport: Bundle): string {
    for (const entry of validationReport.entry ?? []) {
      if ('Parameters' === entry.resource?.resourceType) {
        const parameters: Parameters = entry.resource as Parameters;
        const profileParam = parameters.parameter?.find(p => p.name === "profile");
        const profileUrl = profileParam?.valueCanonical ?? profileParam?.valueUrl;
        if (profileUrl) {
          return profileUrl;
        }
      }
    }

    return 'N/A';
  }

  /**
   * Get the validator used to generate the validation report.
   *
   * @param validationReport the validation report bundle.
   */
  function getValidatorId(validationReport: Bundle): string | undefined {
    for (const entry of validationReport.entry ?? []) {
      if (entry.resource?.resourceType === 'Parameters') {
        const parameters = entry.resource as Parameters;
        const validatorParam = parameters.parameter?.find(parameter => parameter.name === 'validatorId');
        const validatorId = validatorParam?.valueString ?? (validatorParam as any)?.valueId;
        if (validatorId) {
          return validatorId;
        }
      }
    }

    return undefined;
  }

  /**
   * Find a validator exposing the profile used in the report when the report
   * does not contain the validator identifier.
   *
   * @param profileUrl the validated profile URL.
   */
  async function findValidatorIdForProfile(profileUrl: string): Promise<string | undefined> {
    if (profileUrl === 'N/A') {
      return undefined;
    }

    try {
      const response = await fhirClient.search({
        resourceType: 'Device',
        searchParams: {
          type: 'http://fyrstain.com/pdt/ValueSet/systemTypes|validator',
          _elements: 'id'
        }
      });

      for (const entry of (response as Bundle).entry ?? []) {
        const id = entry.resource?.id;
        if (!id) {
          continue;
        }

        try {
          const profiles = await fhirClient.operation({
            name: '$profiles',
            method: 'GET',
            input: { validatorId: id }
          }) as Parameters;
          const supportsProfile = profiles.parameter?.some(parameter =>
            (parameter.valueCanonical ?? parameter.valueUrl ?? parameter.valueString) === profileUrl
          );
          if (supportsProfile) {
            return id;
          }
        } catch (error) {
          console.warn(`Unable to load profiles for validator ${id}`, error);
        }
      }
    } catch (error) {
      console.warn('Unable to resolve a validator for the validation report profile', error);
    }

    return undefined;
  }

  /**
* Get issues to populate the notification.
* 
* @param validationReport    the bundle to the Validation report.
*/
  function getIssues(validationReport: Bundle) {
    const result: OperationOutcomeIssue[] = [];
    validationReport.entry?.forEach(entry => {
      if ('OperationOutcome' === entry.resource?.resourceType) {
        const operationOutcome: OperationOutcome = entry.resource as unknown as OperationOutcome;
        operationOutcome.issue?.forEach(issue => {
          result.push(issue);
        });
      }
    })
    return result.slice(1);
  }

  /**
* Count the percentage of the differents status of the issues to populate the progressBar.
* 
* @param severityStatus    the severity status to the Validation report bundle.
*/
  function countPercentageStatusIssues(severityStatus: string): number {
    const severity = issues.map(s => s.severity);
    return severity.length ? severity.filter(s => s === severityStatus).length * 100 / severity.length : 0;
  }

  /**
* Count the number of the differents status of the issues to populate the progressBar.
* 
* @param severityStatus    the severity status to the Validation report bundle.
*/
  function countStatusIssues(severityStatus: string): number {
    return issues.map(s => s.severity).filter(s => s === severityStatus).length;
  }

  function toggleIssueSeverity(severity: string) {
    if (countStatusIssues(severity) === 0) {
      return;
    }
    setSelectedIssueSeverity(currentSeverity => currentSeverity === severity ? undefined : severity);
    setIssuesExpanded(true);
  }

  /**
* Get Validation Reponse to populate the status of the information notification.
* 
* @param validationReport    the bundle to the Validation report.
*/
  function getValidationReponse(validationReport: Bundle) {
    let validationStatus = 'N/A';
    validationReport.entry?.forEach(entry => {
      if ('OperationOutcome' === entry.resource?.resourceType) {
        const operationOutcome: OperationOutcome = entry.resource as unknown as OperationOutcome;
        const code = operationOutcome.issue
          ?.find(issue => issue.details?.coding?.find(coding => coding.code && (coding.system === "http://isis.com/test/CodeSystem/COS-ValidateStatus" || coding.system === "http://hl7.org/fhir/ValueSet/verificationresult-validation-status")))
          ?.details?.coding?.find(coding => coding.code && (coding.system === "http://isis.com/test/CodeSystem/COS-ValidateStatus" || coding.system === "http://hl7.org/fhir/ValueSet/verificationresult-validation-status"))?.code;
        validationStatus = code ? code : validationStatus;
      }
    })
    return validationStatus;
  }

  /**
  * Get raw content to populate the section.
  * 
  * @param validationReport    the bundle to the Validation report.
  */
  function getRawContent(validationReport: Bundle): string {
    let rawContent = 'N/A';
    validationReport.entry?.forEach(entry => {
      if ('Parameters' === entry.resource?.resourceType) {
        const parameters: Parameters = entry.resource as Parameters;
        const valueString = parameters.parameter?.find(p => p.name === "resource")?.valueString;
        rawContent = valueString ? valueString : 'N/A';
      }
    })
    return rawContent;
  }

  /**
   * Check whether the raw validation resource can be displayed as JSON.
   *
   * @param content the raw resource content.
   */
  function isValidJson(content: string): boolean {
    try {
      JSON.parse(content);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Capitalize the first letter 
   * 
   * @param string the string to capitalize
   */
  function capitalizeFirst(string: string): string {
    const splitted = string.split('');
    splitted[0] = splitted[0].toUpperCase();
    return splitted.join('');
  }

  const rawContent = getRawContent(validationReport);
  const rawContentIsJson = rawContent !== 'N/A' && isValidJson(rawContent);
  const profileUrl = getProfileUrl(validationReport);
  const canRevalidate = profileUrl !== 'N/A' && validatorId !== undefined;
  const displayedIssues = selectedIssueSeverity
    ? issues.filter(issue => issue.severity === selectedIssueSeverity)
    : issues;

  return (
    <PandoraPage titleKey='title.validationreport' loading={loading} needsLogin={false} >
      <>
        <Card>
          <Card.Header>
            <Title level={2} content={'Informations'} />
          </Card.Header>
          <Card.Body className="cardBody">
            <div className={styles.alertContainer}>
              <StatusAlert
                status={FhirStatus[getValidationReponse(validationReport) as keyof typeof FhirStatus]}
              >
                <div className={styles.alertContainer}>
                  <div>
                    <StatusIcon
                      status={FhirStatus[getValidationReponse(validationReport) as keyof typeof FhirStatus]}
                    />
                    <strong className={styles.alertTitle}>
                      {`${capitalizeFirst(getValidationReponse(validationReport))} validation`}
                    </strong>
                  </div>
                </div>
              </StatusAlert>
            </div>
            <div className={styles.form}>
              <div className={styles.formDetails}>
                <div className={styles.formTextLabel}>
                  <Form.Label>
                    <strong className={styles.label}>
                      ID :
                    </strong>
                  </Form.Label>
                  <Form.Text >
                    {validationReport.id ? validationReport.id : 'N/A'}
                  </Form.Text>
                </div>
                <div className={styles.formTextLabel}>
                  <Form.Label>
                    <strong className={styles.label}>
                      {i18n.t('label.profileurl')} :
                    </strong>
                  </Form.Label>
                  <Form.Text >
                    {getProfileUrl(validationReport)}
                  </Form.Text>
                </div>
                <div className={styles.formTextLabel}>
                  <Form.Label>
                    <strong className={styles.label}>
                      Date :
                    </strong>
                  </Form.Label>
                  <Form.Text >
                    {validationReport.timestamp ? validationReport.timestamp : 'N/A'}
                  </Form.Text>
                </div>
                <div className={styles.formTextLabel}>
                  <Form.Label>
                    <strong className={styles.label}>
                      Test ?
                    </strong>
                  </Form.Label>
                  <Form.Text className={testReport ? '' : 'text-danger'}>
                    {testReport === undefined && <span>{i18n.t('infomessage.searchingtestreport')}</span>}
                    {testReport === null && <span>{i18n.t('errormessage.notlinkedvalidation')}</span>}
                    {testReport && <span>{testReport.id} / {testReport.name}</span>}
                  </Form.Text>
                </div>
              </div>
              {canRevalidate && (
                <Button
                  className={`button ${styles.revalidateButton}`}
                  variant="primary"
                  onClick={() => toValidator(validatorId, profileUrl)}
                >
                  {i18n.t('button.revalidate')}
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
        <Card className={styles.issues}>
          <Card.Header>
            <Title level={2} content={i18n.t("title.issues")} />
          </Card.Header>
          <Card.Body>
            <div className={styles.formTextLabel}>
              <div className={styles.summaryStatusContainer}>
                <Form.Label>
                  <strong className={styles.summaryStatusLabel}>
                    {i18n.t("label.statussummary")} :
                  </strong>
                </Form.Label>
                <div className={styles.progressBarContainer}>
                  <Form.Text>
                    <ProgressBar>
                      {['warning', 'error', 'information', 'fatal'].map(severity => {
                        const count = countStatusIssues(severity);
                        const isSelected = selectedIssueSeverity === severity;
                        const variant = severity === 'error'
                          ? 'danger'
                          : severity === 'information'
                            ? 'info'
                            : severity === 'fatal'
                              ? 'dark'
                              : 'warning';
                        return (
                          <ProgressBar
                            key={severity}
                            variant={variant}
                            now={countPercentageStatusIssues(severity)}
                            label={count}
                            className={`${styles.summarySegment} ${isSelected ? styles.summarySegmentActive : ''}`}
                            onClick={() => toggleIssueSeverity(severity)}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleIssueSeverity(severity);
                              }
                            }}
                            role="button"
                            tabIndex={count ? 0 : -1}
                            aria-pressed={isSelected}
                            aria-label={`${capitalizeFirst(severity)}: ${count}`}
                          />
                        );
                      })}
                    </ProgressBar>
                  </Form.Text>
                </div>
              </div>
              <Button
                className={styles.issuesToggle}
                variant="link"
                onClick={() => setIssuesExpanded(expanded => !expanded)}
                aria-expanded={issuesExpanded}
                aria-controls="validation-issues-details"
              >
                <FontAwesomeIcon icon={issuesExpanded ? faChevronUp : faChevronDown} />
                {i18n.t(issuesExpanded ? 'button.hideissues' : 'button.showissues')}
              </Button>
            </div>
            <Collapse in={issuesExpanded}>
              <div id="validation-issues-details">
                {displayedIssues.map((issue, index) =>
                  <div className={styles.alert} key={`${issue.severity}-${issue.code}-${index}`}>
                    <StatusAlert
                      status={FhirStatus[issue.severity as keyof typeof FhirStatus]}
                    >
                      <div className={styles.alertContainer}>
                        <div>
                          <StatusIcon
                            status={FhirStatus[issue.severity as keyof typeof FhirStatus]}
                          />
                          <strong className={styles.alertTitle}>
                            {capitalizeFirst(issue.severity)}
                          </strong>
                        </div>
                        <div className={styles.notificationContent}>
                          <div className={styles.labelsNotification}>
                            <div className={styles.formTextLabel}>
                              <Form.Label>
                                <strong className={styles.label}>
                                  Code :
                                </strong>
                              </Form.Label>
                              <Form.Text >
                                {issue.code ? issue.code : 'N/A'}
                              </Form.Text>
                            </div>
                            <div className={styles.formTextLabel}>
                              <Form.Label>
                                <strong className={styles.label}>
                                  Diagnostics :
                                </strong>
                              </Form.Label>
                              <Form.Text >
                                {issue.diagnostics ? issue.diagnostics : 'N/A'}
                              </Form.Text>
                            </div>
                            {issue.details?.text !== undefined &&
                              <div className={styles.formTextLabel}>
                                <Form.Label>
                                  <strong className={styles.label}>
                                    Details :
                                  </strong>
                                </Form.Label>
                                <Form.Text >
                                  {issue.details?.text ? issue.details?.text : 'N/A'}
                                </Form.Text>
                              </div>
                            }
                            {issue.location !== undefined &&
                              <div className={styles.formTextLabel}>
                                <Form.Label>
                                  <strong className={styles.label}>
                                    Expression :
                                  </strong>
                                </Form.Label>
                                <Form.Text >
                                  {issue.location[0] ? issue.location[0] : 'N/A'}
                                </Form.Text>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </StatusAlert>
                  </div>
                )}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
        <Card className={styles.issues}>
          <Card.Header>
            <Title level={2} content={i18n.t("title.rawcontent")} />
          </Card.Header>
          <Card.Body>
            {rawContent === 'N/A' && <div>{i18n.t('general.notavailable')}</div>}
            {rawContentIsJson && (
              <div className={styles.jsonViewer}>
                <JsonViewer src={JSON.parse(rawContent)} />
              </div>
            )}
            {rawContent !== 'N/A' && !rawContentIsJson && (
              <pre className={styles.rawText}>{rawContent}</pre>
            )}
          </Card.Body>
        </Card>
      </>
    </PandoraPage>
  );
};

export default ValidationReport;
